// Deciding whether a trace id falls inside a sampling ratio, shared by the
// traceparent flags byte and the Sentry-subset span in
// AmplifyApiUtils. Kept dependency-free (no other project imports) so it's
// trivial to reason about and to regenerate __fixtures__/trace-sample-vector
// from, in isolation.

const ZERO = BigInt(0);
const ONE = BigInt(1);
const SIXTY_THREE = BigInt(63);

/**
 * Mirrors backend/lib/trace/sampler.go's `ratioUpperBound` structurally, not
 * just numerically. `ratio * 2**63` is IEEE-754 float64 multiplication in
 * both languages — identical inputs give a bit-identical result — and the
 * product is always an already-integer-valued double once it's that large
 * (a double can't represent a fractional part at that magnitude), so
 * `Math.floor` is a no-op and `BigInt(...)` of it is exact. An earlier
 * version of this function scaled the ratio into a 53-bit integer and
 * shifted it into 63-bit range instead, which is NOT the same computation as
 * Go's direct multiply — it diverges from `ratioUpperBound` at some trace
 * ids/ratios (e.g. ratio 0.1, trace id ...1999999999999800 in the low 8
 * bytes: old bound 922337203685477376, Go/correct bound
 * 922337203685477632 — see bucket.test.ts).
 */
const ratioUpperBound = (ratio: number): bigint => {
  if (ratio >= 1) {
    return ONE << SIXTY_THREE;
  }
  if (ratio <= 0) {
    return ZERO;
  }
  return BigInt(Math.floor(ratio * 2 ** 63));
};

/**
 * Bit-identical to OpenTelemetry Go's `TraceIDRatioBased` sampler: true iff
 * the low 8 bytes of the trace id, right-shifted by 1 (i.e. treated as an
 * unsigned int63), fall below `ratio * 2**63`.
 */
export const bucket = (traceIdHex: string, ratio: number): boolean => {
  const low63 = BigInt(`0x${traceIdHex.slice(16, 32)}`) >> ONE;
  return low63 < ratioUpperBound(ratio);
};
